import { tzOpts } from "@/lib/tz";
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  MessageSquare, Ticket, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import { PausedSessionsPanel } from "@/components/PausedSessionsPanel";
import { AttendanceReport } from "@/components/ChatExtras";
import { useAuth } from "@/contexts/AuthContext";

const PAGE_SIZE = 25;

const PRIORITY_STYLES = {
  alta: "bg-red-500/15 text-red-300 border border-red-500/30",
  normal: "bg-sky-500/15 text-sky-300 border border-sky-500/30",
  baixa: "bg-slate-500/15 text-slate-400 border border-slate-500/30",
};

const STATUS_STYLES = {
  aberto: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  fechado: "bg-slate-500/15 text-slate-400 border border-slate-500/30",
};

function Pagination({ page, pages, total, onGo }) {
  if (total === 0) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800 flex-wrap gap-2">
      <div className="text-xs text-slate-500">
        Página {page} de {pages} · {total} no total
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onGo(1)} disabled={page === 1} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronsLeft className="w-4 h-4" />
        </button>
        <button onClick={() => onGo(page - 1)} disabled={page === 1} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-400 px-2 font-mono-jb">{page} / {pages}</span>
        <button onClick={() => onGo(page + 1)} disabled={page === pages} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button onClick={() => onGo(pages)} disabled={page === pages} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function ConversasTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/chat-logs", { params: { page: p, limit: PAGE_SIZE, search } });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input
          data-testid="chatlogs-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(1)}
          placeholder="Buscar por nome, número ou texto…"
          className="bg-slate-950 border-slate-800 text-white pl-8 h-9 max-w-md"
        />
      </div>
      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-3">Data</th>
                <th className="text-left py-2 px-3">Cliente</th>
                <th className="text-left py-2 px-3">Número</th>
                <th className="text-left py-2 px-3">Texto recebido</th>
              </tr>
            </thead>
            <tbody data-testid="chatlogs-tbody">
              {loading && <tr><td colSpan={4} className="py-8 text-center text-slate-500">Carregando…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-slate-500">
                  {search ? "Nenhuma conversa encontrada com esse filtro." : "Nenhuma conversa registrada ainda."}
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r._id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                  <td className="py-2 px-3 font-mono-jb text-xs text-slate-400 whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("pt-BR", tzOpts())}
                  </td>
                  <td className="py-2 px-3 text-white">{r.client_name || <span className="text-slate-600">—</span>}</td>
                  <td className="py-2 px-3 font-mono-jb text-slate-400">{r.phone}</td>
                  <td className="py-2 px-3 text-slate-300">{r.text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onGo={load} />
      </Card>
    </div>
  );
}

function ChamadosTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/support-tickets", {
        params: { page: p, limit: PAGE_SIZE, search, status: status === "all" ? "" : status },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(data.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="tickets-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(1)}
            placeholder="Buscar por login, nome ou nº do chamado…"
            className="bg-slate-950 border-slate-800 text-white pl-8 h-9"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="tickets-status-filter" className="bg-slate-950 border-slate-800 text-white h-9 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-200">
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="aberto">Aberto</SelectItem>
            <SelectItem value="fechado">Fechado</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="bg-slate-900/50 border-slate-800 p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-3">Abertura</th>
                <th className="text-left py-2 px-3">Chamado</th>
                <th className="text-left py-2 px-3">Cliente</th>
                <th className="text-left py-2 px-3">Assunto</th>
                <th className="text-left py-2 px-3">Prioridade</th>
                <th className="text-left py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="tickets-tbody">
              {loading && <tr><td colSpan={6} className="py-8 text-center text-slate-500">Carregando…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-slate-500">
                  <Ticket className="w-6 h-6 mx-auto mb-2 text-slate-700" />
                  Nenhum chamado encontrado.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                  <td className="py-2 px-3 font-mono-jb text-xs text-slate-400 whitespace-nowrap">
                    {r.abertura ? new Date(r.abertura).toLocaleString("pt-BR", tzOpts()) : "—"}
                  </td>
                  <td className="py-2 px-3 font-mono-jb text-slate-500 text-xs">{r.chamado}</td>
                  <td className="py-2 px-3">
                    <div className="text-white">{r.nome || "—"}</div>
                    <div className="text-[11px] text-slate-500 font-mono-jb">{r.login}</div>
                  </td>
                  <td className="py-2 px-3 text-slate-300">{r.assunto}</td>
                  <td className="py-2 px-3">
                    <Badge className={PRIORITY_STYLES[r.prioridade] || PRIORITY_STYLES.normal}>
                      {r.prioridade || "normal"}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">
                    <Badge className={STATUS_STYLES[r.status] || STATUS_STYLES.aberto}>
                      {r.status === "fechado" ? "Fechado" : "Aberto"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pages={pages} total={total} onGo={load} />
      </Card>
    </div>
  );
}

export default function ChatLogs() {
  const { user } = useAuth();
  const canReport = ["admin", "superadmin"].includes(user?.team_role || "admin") || (user?.perms || []).includes("chat.report");
  return (
    <div className="space-y-8">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Chatbot</div>
        <h1 className="font-outfit text-4xl font-bold tracking-tight text-white mt-1">Atendimentos</h1>
        <p className="text-sm text-slate-500 mt-2">
          Conversas recebidas pelo WhatsApp e os chamados de atendimento abertos a partir delas.
        </p>
      </header>

      <PausedSessionsPanel />

      <Tabs defaultValue="conversas">
        <TabsList className="bg-slate-900/50 border border-slate-800">
          <TabsTrigger value="conversas" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Conversas
          </TabsTrigger>
          <TabsTrigger value="chamados" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            <Ticket className="w-3.5 h-3.5 mr-1.5" /> Chamados
          </TabsTrigger>
          {canReport && <TabsTrigger value="relatorio" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">
            Relatório
          </TabsTrigger>}
        </TabsList>
        <TabsContent value="conversas" className="mt-6">
          <ConversasTab />
        </TabsContent>
        <TabsContent value="chamados" className="mt-6">
          <ChamadosTab />
        </TabsContent>
        {canReport && (
          <TabsContent value="relatorio" className="mt-6">
            <AttendanceReport />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
